import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { Menu, X } from 'lucide-react';
import { Fragment, useEffect, useId, useState, type ReactNode } from 'react';

import { signOutAdmin } from '../features/auth/session';
import { NewBidAlert } from '../features/bids/components/new-bid-alert';
import { useAdminBidStream } from '../features/bids/hooks/use-admin-bid-stream';
import { useSessionRoles } from '../features/auth/use-session-roles';
import { useSessionUser } from '../features/clients/hooks/use-session-user';
import { t } from '../lib/i18n';
import { Toaster } from '../lib/toast';
import {
  MORE_NAV_ITEM,
  bottomNavItems,
  groupNavItems,
  visibleNavItems,
  type NavItem,
  type NavSection,
} from './nav-items';
import { StreamStatusDot } from './stream-status-dot';
import { useNavBadges, type NavBadges } from './use-nav-badges';

/**
 * App shell on the `Redesign-D-Admin{,-Mobile}` artboards.
 *
 * Two shells, one component: a 232px sticky sidebar from `lg` up, and below it
 * the mobile chrome — sticky header, slide-in drawer, fixed bottom bar. The
 * breakpoint is `lg` (1024px) because the artboards are authored at 1440 and
 * 390 with nothing in between; the sidebar stops fitting well before 1024.
 *
 * The 3px accent rule along the very top belongs to both (`.cf-admin-top-rule`)
 * — it is the mark that says "operator tool", and the one piece of chrome the
 * two viewports share exactly.
 */
export function Layout({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const [menuOpen, setMenuOpen] = useState(false);
  const { user } = useSessionUser();
  // The real roles from `GET /users/me` (shared cache with RequireAdmin). Until
  // they arrive this is a plain admin, so no superuser row flashes in.
  const { roles } = useSessionRoles();
  const badges = useNavBadges(roles);
  const sections = groupNavItems(visibleNavItems(roles));
  const bottomItems = bottomNavItems(roles);
  // The ONE EventSource of the SPA: toasts, the alert pill and the realtime
  // dot all read from this instance (a second call would double every alarm).
  const stream = useAdminBidStream({ selfAdminId: user?._id });

  // A navigation inside the drawer must close it; without this the operator
  // taps a destination and the drawer stays over the screen they asked for.
  useEffect(() => setMenuOpen(false), [pathname]);

  // The drawer is a modal layer: the page behind it must not scroll under it.
  useEffect(() => {
    if (!menuOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [menuOpen]);

  const displayName = user?.displayName || user?.email || '';
  const initials = toInitials(displayName);
  const renderedAt = new Intl.DateTimeFormat('uk-UA', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date());

  return (
    <div className="cf-admin-top-rule min-h-screen bg-cf-ground lg:grid lg:grid-cols-[232px_minmax(0,1fr)]">
      {/* ── Desktop sidebar ─────────────────────────────────────────────── */}
      <aside className="sticky top-0 hidden h-screen flex-col border-r border-cf-line bg-cf-surface pt-[3px] lg:flex">
        <div className="flex items-center justify-between border-b border-cf-line-soft px-5 pt-[18px] pb-[14px]">
          <img src="/assets/cf/logo.png" alt="Cars Finders" className="h-[30px] w-auto" />
          <StreamStatusDot connected={stream.connected} disconnectedSince={stream.disconnectedSince} />
        </div>

        <nav className="flex flex-1 flex-col gap-0.5 overflow-auto p-2.5">
          <NavGroupList
            sections={sections}
            renderItem={(item) => <SidebarLink item={item} pathname={pathname} badges={badges} />}
          />
        </nav>

        <div className="flex flex-col gap-1.5 border-t border-cf-line-soft px-2.5 pt-3 pb-4">
          <div className="flex items-center gap-2.5 px-3 py-1.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-cf-brand text-xs font-semibold text-white">
              {initials}
            </span>
            <div className="min-w-0 flex-1 text-[13px] leading-[1.3]">
              <strong className="block truncate">{displayName}</strong>
              <span className="text-xs text-cf-ink-subtle">{t('admin.shell.role')}</span>
            </div>
          </div>
          <SignOutLink className="h-10 px-3 text-sm" />
        </div>
      </aside>

      {/* ── Mobile header ───────────────────────────────────────────────── */}
      <header className="cf-admin-top-rule sticky top-0 z-30 border-b border-cf-line-soft bg-cf-ground/95 backdrop-blur-[6px] lg:hidden">
        <div className="mx-auto flex h-14 max-w-[520px] items-center gap-2 px-4">
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label={t('admin.shell.menu')}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-cf-line bg-cf-surface text-cf-ink"
          >
            <Menu size={18} strokeWidth={1.75} />
          </button>
          <img src="/assets/cf/logo.png" alt="Cars Finders" className="mr-auto h-[26px] w-auto" />
          {/*
            The artboard prints a clock between the logo and the avatar. It is
            not decoration: on a queue screen the operator needs to know how
            stale what they are looking at is. It reads the same source as the
            desktop header's «оновлено» — the moment this shell rendered.
          */}
          <span className="font-mono text-[10px] tracking-[0.1em] text-cf-ink-subtle uppercase">
            {renderedAt}
          </span>
          <StreamStatusDot connected={stream.connected} disconnectedSince={stream.disconnectedSince} />
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-cf-brand text-xs font-semibold text-white">
            {initials}
          </span>
        </div>
      </header>

      {/* ── Mobile drawer ───────────────────────────────────────────────── */}
      {menuOpen && (
        <>
          <div
            onClick={() => setMenuOpen(false)}
            className="fixed inset-0 z-40 bg-cf-ink/40 backdrop-blur-[2px] lg:hidden"
          />
          <nav
            role="dialog"
            aria-modal="true"
            className="fixed inset-y-0 left-0 z-41 flex w-[280px] flex-col border-r border-cf-line bg-cf-surface shadow-[0_0_40px_rgba(0,0,0,.2)] lg:hidden"
          >
            <div className="flex items-center justify-between border-b border-cf-line-soft px-[18px] pt-4 pb-[14px]">
              <img src="/assets/cf/logo.png" alt="" className="h-7 w-auto" />
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                aria-label={t('admin.shell.close')}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-cf-fill"
              >
                <X size={16} strokeWidth={1.75} />
              </button>
            </div>

            <div className="flex flex-1 flex-col gap-0.5 overflow-auto p-2">
              <NavGroupList
                sections={sections}
                renderItem={(item) => <DrawerLink item={item} pathname={pathname} badges={badges} />}
              />
            </div>

            <div className="border-t border-cf-line-soft px-2 pt-2.5 pb-[calc(1rem+env(safe-area-inset-bottom))]">
              <div className="px-3 pt-1.5 pb-2.5 text-[13px]">
                <strong>{displayName}</strong>
                <br />
                <span className="text-xs text-cf-ink-subtle">{t('admin.shell.role')}</span>
              </div>
              <SignOutLink className="h-11 px-3 text-sm" />
            </div>
          </nav>
        </>
      )}

      {/*
        Page padding belongs to the shell, exactly as both artboards draw it:
        `padding:16px` with a 12px column gap on mobile, `35px 32px 64px` with a
        20px gap on desktop. Screens therefore render their sections directly and
        never re-pad — a page that adds its own container doubles the gutter.
      */}
      <main className="mx-auto flex w-full min-w-0 max-w-[520px] flex-col gap-3 p-4 pb-[76px] lg:mx-0 lg:max-w-[1440px] lg:gap-5 lg:px-8 lg:pt-[35px] lg:pb-16">
        {children}
      </main>

      {/* ── Mobile bottom bar ───────────────────────────────────────────── */}
      <nav className="fixed inset-x-0 bottom-0 z-25 border-t border-cf-line bg-cf-surface/95 backdrop-blur-[8px] lg:hidden">
        <div className="mx-auto grid max-w-[520px] grid-cols-5 px-2 pt-1.5 pb-[calc(0.5rem+env(safe-area-inset-bottom))]">
          {bottomItems.map((item) => (
            <BottomLink key={item.to} item={item} pathname={pathname} badges={badges} />
          ))}
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            className="relative flex min-h-[52px] flex-col items-center gap-[3px] px-0.5 py-1.5 text-[10px] font-medium text-cf-ink-subtle"
          >
            <MORE_NAV_ITEM.icon size={22} strokeWidth={1.75} />
            {MORE_NAV_ITEM.label}
          </button>
        </div>
      </nav>

      {/* Live relay-desk alarm: a captured bid the operator has not seen yet. */}
      <NewBidAlert events={stream.events} connected={stream.connected} />
      <Toaster />
    </div>
  );
}

/** Root is only active on an exact match; every other row matches its subtree. */
function isActive(to: string, pathname: string): boolean {
  return to === '/' ? pathname === '/' : pathname.startsWith(to);
}

function badgeFor(item: NavItem, badges: NavBadges): number | undefined {
  const value = item.badgeKey ? badges[item.badgeKey] : undefined;
  return value && value > 0 ? value : undefined;
}

/**
 * The artboard's sectioned menu (Робота · Перевірки · Довідники), shared by the
 * desktop sidebar and the mobile drawer. A heading is plain text, not a link:
 * it takes no tab stop, and its section is a `role="group"` labelled by it, so
 * assistive tech announces the section on entering it. The heading style is the
 * artboard's — mono 10px, .12em tracking, uppercase, subtle ink, 4px above the
 * first section and 14px above the rest.
 */
export function NavGroupList({
  sections,
  renderItem,
}: {
  sections: NavSection[];
  renderItem: (item: NavItem) => ReactNode;
}) {
  // The sidebar and the drawer render this list at the same time (the sidebar
  // is only CSS-hidden below `lg`), so the heading ids need a per-instance prefix.
  const idPrefix = useId();

  return (
    <>
      {sections.map((section, index) => {
        const headingId = `${idPrefix}-nav-${section.group}`;
        return (
          <div
            key={section.group}
            role="group"
            aria-labelledby={headingId}
            data-nav-group={section.group}
            className="flex flex-col gap-0.5"
          >
            <span
              id={headingId}
              className={`px-3 pb-1.5 font-mono text-[10px] leading-[normal] tracking-[0.12em] text-cf-ink-subtle uppercase ${
                index === 0 ? 'pt-1' : 'pt-[14px]'
              }`}
            >
              {section.label}
            </span>
            {section.items.map((item) => (
              <Fragment key={item.to}>{renderItem(item)}</Fragment>
            ))}
          </div>
        );
      })}
    </>
  );
}

function SidebarLink({
  item,
  pathname,
  badges,
}: {
  item: NavItem;
  pathname: string;
  badges: NavBadges;
}) {
  const active = isActive(item.to, pathname);
  const badge = badgeFor(item, badges);
  const Icon = item.icon;

  return (
    <Link
      to={item.to}
      className={`flex h-10 items-center gap-2.5 rounded-cf-md px-3 text-sm transition-colors ${
        active
          ? 'bg-cf-fill font-semibold text-cf-ink'
          : 'font-medium text-cf-ink-muted hover:bg-cf-fill'
      }`}
    >
      <Icon size={18} strokeWidth={1.75} />
      <span className="flex-1">{item.label}</span>
      {badge !== undefined && <NavBadge value={badge} muted={!active} />}
    </Link>
  );
}

function DrawerLink({
  item,
  pathname,
  badges,
}: {
  item: NavItem;
  pathname: string;
  badges: NavBadges;
}) {
  const active = isActive(item.to, pathname);
  const badge = badgeFor(item, badges);

  return (
    <Link
      to={item.to}
      className={`flex h-11 items-center gap-2.5 rounded-cf-md px-3 text-[15px] text-cf-ink ${
        active ? 'bg-cf-fill font-semibold' : 'font-medium'
      }`}
    >
      <span className="flex-1">{item.label}</span>
      {badge !== undefined && <NavBadge value={badge} muted={!active} />}
    </Link>
  );
}

function BottomLink({
  item,
  pathname,
  badges,
}: {
  item: NavItem;
  pathname: string;
  badges: NavBadges;
}) {
  // `/clients/$id` is still "Clients" on the bar — the detail screen is part of
  // that destination, and a bar that goes blank on a detail page reads broken.
  const active = isActive(item.to, pathname);
  const badge = badgeFor(item, badges);
  const Icon = item.icon;

  return (
    <Link
      to={item.to}
      className={`relative flex min-h-[52px] flex-col items-center gap-[3px] px-0.5 py-1.5 text-[10px] ${
        active ? 'font-semibold text-cf-brand' : 'font-medium text-cf-ink-subtle'
      }`}
    >
      <Icon size={22} strokeWidth={1.75} />
      {item.label}
      {badge !== undefined && (
        <span className="absolute top-1 right-[calc(50%-20px)] rounded-full bg-cf-brand px-[5px] py-px font-mono text-[9px] text-white">
          {badge}
        </span>
      )}
    </Link>
  );
}

function NavBadge({ value, muted }: { value: number; muted: boolean }) {
  return (
    <span
      className={`rounded-full px-[7px] py-0.5 font-mono text-[11px] ${
        muted ? 'bg-cf-fill text-cf-ink-muted' : 'bg-cf-brand text-white'
      }`}
    >
      {value}
    </span>
  );
}

/**
 * Ends the session through apps/api (`auth/admin/logout` clears the NextAuth
 * cookie, chunks included) and lands on this SPA's own /login — it used to
 * leave for the auction's sign-out page and never come back.
 */
function SignOutLink({ className = '' }: { className?: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const signOut = async () => {
    await signOutAdmin();
    queryClient.clear();
    await navigate({ to: '/login', replace: true });
  };
  return (
    <button
      type="button"
      onClick={() => void signOut()}
      className={`flex w-full cursor-pointer items-center gap-2.5 rounded-cf-md text-left font-semibold text-cf-danger hover:bg-cf-danger-tint ${className}`}
    >
      {t('admin.shell.signOut')}
    </button>
  );
}

/** Two letters for the avatar — the artboard draws "ОК" for Олена Коваль. */
function toInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}
